import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import SaveIcon from '@mui/icons-material/Save';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import formatDate from '../../components/formatDate';
import {
  createCuratedArticle,
  cropCuratedArticleCover,
  deleteCuratedArticle,
  generateCuratedArticle,
  getCuratedArticle,
  getCuratedArticlesPayload,
  isCuratedArticlesApiSuccess,
  publishCuratedArticle,
  unpublishCuratedArticle,
  updateCuratedArticle,
  uploadCuratedArticleImage,
} from '../../services/curatedArticlesApi';
import CoverCropPanel from './components/CoverCropPanel';
import DiscoverCardPreview from './components/DiscoverCardPreview';
import PublishChecklist from './components/PublishChecklist';
import StatusBadge from './components/StatusBadge';
import {
  getArticleCoverImageUrl,
  getArticleCoverTitle,
  getArticleCropSourceImageUrl,
  getArticleDeckSummary,
  getArticleGenerationError,
  getArticleGenerationPrompt,
  getArticleHotTake,
  getArticleHotTakeOptions,
  getArticleScrapeHeroUrl,
  getArticleSlideCount,
  getArticleUpdatedAt,
  getCoverCropResultImageUrl,
  getUploadImageUrl,
  clampDeckSummary,
  SUMMARY_MAX_CHARS,
  withImageCacheBust,
} from './utils/articleFieldHelpers';

const GENERATING_STATUSES = ['scraping', 'generating'];
const SLIDE_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const TONE_PLACEHOLDER =
  'Focus on Ireland’s historic win. Mention Tucker and Humphreys. Keep tone dramatic.';
const TONE_HELPER =
  'Optional AI instructions — tone, focus, names. Saved on the article and sent with Generate.';
const DELETE_ARTICLE_CONFIRM =
  'Delete this article? It will be removed from the list and Pulse after unpublish/archive.';
const HOT_TAKE_MAX = 70;

const emptyCreateForm = () => ({
  sourceUrl1: '',
  sourceUrl2: '',
  sourceUrl3: '',
  slideCount: 5,
  generationPrompt: '',
  sortOrder: 100,
  competitionCid: '',
  startsAt: '',
  endsAt: '',
});

const buildFormFromArticle = (data, prev = {}) => {
  const urls = data?.sourceUrls || data?.source_urls || [];
  return {
    coverTitle: getArticleCoverTitle(data),
    coverImageUrl: getArticleCoverImageUrl(data),
    summary: getArticleDeckSummary(data),
    hotTake: getArticleHotTake(data),
    hotTakeOptions: getArticleHotTakeOptions(data),
    slideCount: getArticleSlideCount(data),
    generationPrompt: getArticleGenerationPrompt(data),
    sortOrder: data?.sortOrder ?? data?.sort_order ?? 100,
    startsAt: data?.startsAt || data?.starts_at
      ? String(data.startsAt || data.starts_at).slice(0, 16)
      : '',
    endsAt: data?.endsAt || data?.ends_at
      ? String(data.endsAt || data.ends_at).slice(0, 16)
      : '',
    sourceUrl1: urls[0] || prev.sourceUrl1 || '',
    sourceUrl2: urls[1] || prev.sourceUrl2 || '',
    sourceUrl3: urls[2] || prev.sourceUrl3 || '',
  };
};

function ArticleEditor() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isNew = pathname.endsWith('/new');
  const articleId = isNew ? null : id;
  const pollRef = useRef(null);
  const coverFileInputRef = useRef(null);

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [croppingCover, setCroppingCover] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [form, setForm] = useState(buildFormFromArticle(null));
  const [cropSourceImageUrl, setCropSourceImageUrl] = useState('');
  const [coverPreviewKey, setCoverPreviewKey] = useState(0);
  const savedSourceUrlsRef = useRef([]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSessionExpired = () => {
    showSnackbar('Session expired. Please login again.', 'error');
    setTimeout(() => {
      window.location = '/login';
    }, 2000);
  };

  const applyArticleToState = useCallback((data) => {
    setArticle(data);
    const next = buildFormFromArticle(data);
    setForm(next);
    savedSourceUrlsRef.current = [next.sourceUrl1, next.sourceUrl2, next.sourceUrl3]
      .map((u) => u.trim())
      .filter(Boolean);
    setCropSourceImageUrl((prev) => {
      const preferred = getArticleCropSourceImageUrl(data);
      if (preferred && preferred !== next.coverImageUrl) return preferred;
      if (prev && prev !== next.coverImageUrl) return prev;
      return preferred || next.coverImageUrl || '';
    });
  }, []);

  const loadArticle = useCallback(
    async (targetId, { silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const res = await getCuratedArticle(targetId);
        if (res.statusCode === '10003') {
          handleSessionExpired();
          return null;
        }
        if (!isCuratedArticlesApiSuccess(res)) {
          showSnackbar(res.message || 'Failed to load article', 'error');
          return null;
        }
        const data = getCuratedArticlesPayload(res);
        applyArticleToState(data);
        return data;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [applyArticleToState]
  );

  useEffect(() => {
    if (!isNew && articleId) loadArticle(articleId);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [articleId, isNew, loadArticle]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateHotTakeOption = (index, value) => {
    setForm((prev) => {
      const hotTakeOptions = [...(prev.hotTakeOptions || [])];
      hotTakeOptions[index] = value;
      return { ...prev, hotTakeOptions };
    });
  };

  const addHotTakeOption = () => {
    setForm((prev) => ({
      ...prev,
      hotTakeOptions: [...(prev.hotTakeOptions || []), ''],
    }));
  };

  const removeHotTakeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      hotTakeOptions: (prev.hotTakeOptions || []).filter((_, i) => i !== index),
    }));
  };

  const pollUntilDone = (targetId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const res = await getCuratedArticle(targetId);
      if (!isCuratedArticlesApiSuccess(res)) return;
      const data = getCuratedArticlesPayload(res);
      applyArticleToState(data);
      if (!GENERATING_STATUSES.includes(data.status)) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setGenerating(false);
        if (data.status === 'review') {
          showSnackbar('Generation complete — ready for review');
        } else if (data.status === 'failed') {
          showSnackbar(getArticleGenerationError(data) || 'Generation failed', 'error');
        }
      }
    }, 3000);
  };

  const handleCreate = async () => {
    const sourceUrls = [createForm.sourceUrl1, createForm.sourceUrl2, createForm.sourceUrl3]
      .map((u) => u.trim())
      .filter(Boolean);

    if (sourceUrls.length === 0) {
      showSnackbar('At least one source URL is required', 'error');
      return;
    }
    if (sourceUrls.length > 3) {
      showSnackbar('Maximum 3 source URLs', 'error');
      return;
    }

    setSaving(true);
    const body = {
      sourceUrls,
      slideCount: Number(createForm.slideCount) || 5,
      generationPrompt: createForm.generationPrompt.trim(),
      sortOrder: Number(createForm.sortOrder) || 100,
      competitionCid: createForm.competitionCid.trim() || null,
      startsAt: createForm.startsAt ? new Date(createForm.startsAt).toISOString() : null,
      endsAt: createForm.endsAt ? new Date(createForm.endsAt).toISOString() : null,
    };

    const res = await createCuratedArticle(body);
    setSaving(false);

    if (res.statusCode === '10003') {
      handleSessionExpired();
      return;
    }
    if (isCuratedArticlesApiSuccess(res)) {
      const created = getCuratedArticlesPayload(res);
      showSnackbar('Article created');
      navigate(`/admin/curated-articles/${created.id}`, { replace: true });
    } else {
      showSnackbar(res.message || 'Failed to create article', 'error');
    }
  };

  const getCurrentSourceUrls = () =>
    [form.sourceUrl1, form.sourceUrl2, form.sourceUrl3].map((u) => u.trim()).filter(Boolean);

  const sourceUrlsWereEdited = (currentUrls) => {
    const saved = savedSourceUrlsRef.current;
    if (currentUrls.length !== saved.length) return true;
    return currentUrls.some((url, i) => url !== saved[i]);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const generateBody = {
      generationPrompt: form.generationPrompt.trim(),
    };
    const slideCount = Number(form.slideCount);
    if (slideCount >= 1 && slideCount <= 10) {
      generateBody.slideCount = slideCount;
    }

    const sourcesEditable = article?.status !== 'published';
    const sourceUrls = getCurrentSourceUrls();
    if (sourcesEditable) {
      if (sourceUrls.length === 0) {
        showSnackbar('At least one source URL is required', 'error');
        setGenerating(false);
        return;
      }
      generateBody.sourceUrls = sourceUrls;
    } else if (sourceUrlsWereEdited(sourceUrls)) {
      showSnackbar('Unpublish to change source URLs before regenerating', 'error');
      setGenerating(false);
      return;
    }

    const res = await generateCuratedArticle(articleId, generateBody);
    if (res.statusCode === '10003') {
      handleSessionExpired();
      setGenerating(false);
      return;
    }
    if (!isCuratedArticlesApiSuccess(res)) {
      showSnackbar(res.message || 'Generation failed', 'error');
      setGenerating(false);
      return;
    }
    const data = getCuratedArticlesPayload(res);
    applyArticleToState(data);
    if (GENERATING_STATUSES.includes(data.status)) {
      showSnackbar('Generation started — this may take 30–90 seconds');
      pollUntilDone(articleId);
    } else if (data.status === 'review') {
      setGenerating(false);
      showSnackbar('Generation complete');
    } else if (data.status === 'failed') {
      setGenerating(false);
      showSnackbar(getArticleGenerationError(data) || 'Generation failed', 'error');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const isPublished = article?.status === 'published';
    const summary = clampDeckSummary(form.summary, SUMMARY_MAX_CHARS);
    const body = {
      coverTitle: form.coverTitle.trim() || undefined,
      coverImageUrl: form.coverImageUrl.trim() || undefined,
      summary,
      deckSummary: summary,
      deck_summary: summary,
      slideCount: Number(form.slideCount) || 5,
      generationPrompt: form.generationPrompt.trim(),
      sortOrder: Number(form.sortOrder),
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      hotTake: form.hotTake.trim() || null,
      hot_take: form.hotTake.trim() || null,
      hotTakeOptions: (form.hotTakeOptions || []).map((o) => String(o).trim()).filter(Boolean),
      hot_take_options: (form.hotTakeOptions || []).map((o) => String(o).trim()).filter(Boolean),
    };

    if (!isPublished) {
      const sourceUrls = getCurrentSourceUrls();
      if (sourceUrls.length) body.sourceUrls = sourceUrls;
    }

    const res = await updateCuratedArticle(articleId, body);
    setSaving(false);

    if (res.statusCode === '10003') {
      handleSessionExpired();
      return;
    }
    if (isCuratedArticlesApiSuccess(res)) {
      showSnackbar('Article saved');
      await loadArticle(articleId);
    } else {
      showSnackbar(res.message || 'Failed to save', 'error');
    }
  };

  const handleDeleteArticle = async () => {
    if (!window.confirm(DELETE_ARTICLE_CONFIRM)) return;
    setSaving(true);
    const res = await deleteCuratedArticle(articleId);
    setSaving(false);
    if (res.statusCode === '10003') {
      handleSessionExpired();
      return;
    }
    if (isCuratedArticlesApiSuccess(res)) {
      showSnackbar('Article deleted');
      navigate('/admin/curated-articles', { replace: true });
    } else {
      showSnackbar(res.message || 'Failed to delete article', 'error');
    }
  };

  const handlePublish = async () => {
    const errors = {};
    if (!form.coverTitle?.trim()) errors.coverTitle = true;
    if (!form.coverImageUrl?.trim()) errors.coverImageUrl = true;
    setFieldErrors(errors);

    if (errors.coverTitle) {
      showSnackbar('Title is required to publish', 'error');
      return;
    }
    if (errors.coverImageUrl) {
      showSnackbar('Cover image is required — re-run Generate or upload', 'error');
      return;
    }
    const hasPayload =
      !!article?.payload?.briefs?.length ||
      !!article?.briefsFlat?.length ||
      !!article?.briefs_flat?.length;
    if (!hasPayload) {
      showSnackbar('Generate content before publishing', 'error');
      return;
    }

    setSaving(true);
    // Persist title / summary / cover / hot take before publish so Pulse sync picks them up.
    const summary = clampDeckSummary(form.summary, SUMMARY_MAX_CHARS);
    await updateCuratedArticle(articleId, {
      coverTitle: form.coverTitle.trim(),
      coverImageUrl: form.coverImageUrl.trim(),
      summary,
      deckSummary: summary,
      deck_summary: summary,
      hotTake: form.hotTake.trim() || null,
      hot_take: form.hotTake.trim() || null,
      hotTakeOptions: (form.hotTakeOptions || []).map((o) => String(o).trim()).filter(Boolean),
      hot_take_options: (form.hotTakeOptions || []).map((o) => String(o).trim()).filter(Boolean),
    });

    const res = await publishCuratedArticle(articleId);
    setSaving(false);

    if (res.statusCode === '10003') {
      handleSessionExpired();
      return;
    }
    if (isCuratedArticlesApiSuccess(res)) {
      showSnackbar('Published to Pulse (news + hot take). Not shown on Discover.');
      setFieldErrors({});
      await loadArticle(articleId);
    } else {
      showSnackbar(res.message || 'Failed to publish', 'error');
    }
  };

  const handleUnpublish = async () => {
    setSaving(true);
    const res = await unpublishCuratedArticle(articleId);
    setSaving(false);

    if (res.statusCode === '10003') {
      handleSessionExpired();
      return;
    }
    if (isCuratedArticlesApiSuccess(res)) {
      showSnackbar('Unpublished — back to review. Pulse card will drop from the sync window.');
      await loadArticle(articleId);
    } else {
      showSnackbar(res.message || 'Failed to unpublish', 'error');
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadCuratedArticleImage(file);
      if (res.statusCode === '10003') {
        handleSessionExpired();
        return;
      }
      if (!isCuratedArticlesApiSuccess(res)) {
        showSnackbar(res.message || 'Upload failed', 'error');
        return;
      }
      const payload = getCuratedArticlesPayload(res) || {};
      const imageUrl = getUploadImageUrl(payload);
      if (!imageUrl) {
        showSnackbar('Upload finished but image URL was missing.', 'error');
        return;
      }
      updateField('coverImageUrl', imageUrl);
      setCropSourceImageUrl(imageUrl);
      setCoverPreviewKey((k) => k + 1);
      setFieldErrors((f) => ({ ...f, coverImageUrl: false }));
      showSnackbar('Cover uploaded. Save the article to persist.');
    } catch (uploadError) {
      showSnackbar(uploadError.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      if (coverFileInputRef.current) coverFileInputRef.current.value = '';
    }
  };

  const handleCoverCrop = async ({ sourceImageUrl, focusX, focusY, coverTitle }) => {
    const trimmedSource = (sourceImageUrl || '').trim();
    if (!trimmedSource) {
      showSnackbar('Source image URL is required to crop', 'error');
      return;
    }

    setCroppingCover(true);
    try {
      const body = {
        sourceImageUrl: trimmedSource,
        focusY: Number(focusY),
        coverTitle: (coverTitle || form.coverTitle || '').trim() || undefined,
      };
      if (focusX != null && !Number.isNaN(Number(focusX))) {
        body.focusX = Number(focusX);
      }
      const res = await cropCuratedArticleCover(articleId, body);

      if (res.statusCode === '10003') {
        handleSessionExpired();
        return;
      }
      if (!isCuratedArticlesApiSuccess(res)) {
        showSnackbar(res.message || 'Cover crop failed', 'error');
        return;
      }

      const data = getCuratedArticlesPayload(res) || {};
      const nextCoverUrl = getCoverCropResultImageUrl(data);
      if (!nextCoverUrl) {
        showSnackbar('Crop succeeded but no coverImageUrl in response.', 'warning');
        return;
      }

      setArticle((prev) => (prev ? { ...prev, ...data } : data));
      setForm((f) => ({
        ...f,
        coverImageUrl: nextCoverUrl,
        coverTitle: getArticleCoverTitle(data) || f.coverTitle,
        summary: getArticleDeckSummary(data) || f.summary,
      }));
      setCropSourceImageUrl(trimmedSource);
      setCoverPreviewKey((k) => k + 1);
      showSnackbar('Clean cover crop applied (title stays as text)');
    } catch (err) {
      showSnackbar(err?.message || 'Cover crop failed', 'error');
    } finally {
      setCroppingCover(false);
    }
  };

  if (isNew) {
    return (
      <Box>
        <Button
          component={Link}
          to="/admin/curated-articles"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Back to articles
        </Button>
        <Typography variant="h6" gutterBottom>
          Create curated article
        </Typography>
        <Alert severity="info" sx={{ mb: 2, maxWidth: 720 }}>
          After generate + review, publish goes to <strong>Pulse</strong> (news + hot take) — not
          Discover. Unlimited published articles (Pulse news volume).
        </Alert>
        <Card sx={{ maxWidth: 720 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Source URL 1"
                  value={createForm.sourceUrl1}
                  onChange={(e) => setCreateForm((f) => ({ ...f, sourceUrl1: e.target.value }))}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Source URL 2 (optional)"
                  value={createForm.sourceUrl2}
                  onChange={(e) => setCreateForm((f) => ({ ...f, sourceUrl2: e.target.value }))}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Source URL 3 (optional)"
                  value={createForm.sourceUrl3}
                  onChange={(e) => setCreateForm((f) => ({ ...f, sourceUrl3: e.target.value }))}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel>Content slides</InputLabel>
                  <Select
                    label="Content slides"
                    value={createForm.slideCount}
                    onChange={(e) => setCreateForm((f) => ({ ...f, slideCount: e.target.value }))}
                  >
                    {SLIDE_COUNT_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Content slides 1–10 (outro added automatically). Slide images optional; used for
                  generation payload only — not a Discover carousel.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sort order"
                  value={createForm.sortOrder}
                  onChange={(e) => setCreateForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Editor instructions"
                  placeholder={TONE_PLACEHOLDER}
                  value={createForm.generationPrompt}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, generationPrompt: e.target.value }))
                  }
                  helperText={TONE_HELPER}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Competition CID (optional)"
                  value={createForm.competitionCid}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, competitionCid: e.target.value }))
                  }
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Starts at"
                  InputLabelProps={{ shrink: true }}
                  value={createForm.startsAt}
                  onChange={(e) => setCreateForm((f) => ({ ...f, startsAt: e.target.value }))}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Ends at"
                  InputLabelProps={{ shrink: true }}
                  value={createForm.endsAt}
                  onChange={(e) => setCreateForm((f) => ({ ...f, endsAt: e.target.value }))}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleCreate} disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : 'Create draft'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box>
        <Button component={Link} to="/admin/curated-articles" startIcon={<ArrowBackIcon />}>
          Back to articles
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          Curated article not found.
        </Alert>
      </Box>
    );
  }

  const canEditSources = article.status !== 'published';
  const isPublished = article.status === 'published';
  const scrapeHeroUrl = getArticleScrapeHeroUrl(article);
  const coverSrc = withImageCacheBust(form.coverImageUrl, coverPreviewKey || undefined);
  const hasPayload =
    !!article?.payload?.briefs?.length ||
    !!article?.briefsFlat?.length ||
    !!article?.briefs_flat?.length;
  const briefCount =
    article?.briefsFlat?.length ||
    article?.briefs_flat?.length ||
    article?.payload?.briefs?.length ||
    0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton component={Link} to="/admin/curated-articles">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ ml: 1 }}>
            <Typography variant="h6">{form.coverTitle || `Article #${articleId}`}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusBadge status={article.status} />
              {briefCount > 0 && (
                <Chip size="small" variant="outlined" label={`${briefCount} slides in payload`} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Edit like a Pulse news card: <strong>title</strong>, <strong>summary</strong> (same text as
        Pulse), <strong>cover</strong>, <strong>hot take</strong>, then publish. Slides stay in the
        generation payload — this screen is not a Discover story carousel.
      </Alert>

      {getArticleGenerationError(article) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getArticleGenerationError(article)}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={
            generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
          }
          onClick={handleGenerate}
          disabled={generating || GENERATING_STATUSES.includes(article.status)}
        >
          Generate
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || generating}
        >
          Save
        </Button>
        {isPublished ? (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<UnpublishedIcon />}
            onClick={handleUnpublish}
            disabled={saving}
          >
            Unpublish
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            startIcon={<PublishIcon />}
            onClick={handlePublish}
            disabled={saving || article.status !== 'review'}
          >
            Publish to Pulse
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Card details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Title"
                value={form.coverTitle}
                error={!!fieldErrors.coverTitle}
                onChange={(e) => {
                  setFieldErrors((f) => ({ ...f, coverTitle: false }));
                  updateField('coverTitle', e.target.value);
                }}
                margin="dense"
                helperText="Pulse news card title (coverTitle) — shown as text, not on the cover image"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Status"
                value={article.status}
                margin="dense"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Summary"
                value={form.summary}
                onChange={(e) => updateField('summary', e.target.value.slice(0, SUMMARY_MAX_CHARS))}
                margin="dense"
                multiline
                minRows={2}
                helperText={`${String(form.summary || '').length}/${SUMMARY_MAX_CHARS} — Pulse news body (140–150 target, max ${SUMMARY_MAX_CHARS}). Same string the app shows under the title. No trailing …`}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Cover image URL"
                value={form.coverImageUrl}
                error={!!fieldErrors.coverImageUrl}
                onChange={(e) => {
                  setFieldErrors((f) => ({ ...f, coverImageUrl: false }));
                  const nextUrl = e.target.value;
                  updateField('coverImageUrl', nextUrl);
                  setCropSourceImageUrl((prev) => prev || nextUrl);
                }}
                margin="dense"
                helperText="Wide 5:2 Pulse cover (clean photo). Title is separate text — not burned onto the image."
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : 'Upload'}
                  <input
                    ref={coverFileInputRef}
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleCoverUpload}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  POST /admin/curated-articles/upload-image → then save
                </Typography>
              </Box>
              {coverSrc ? (
                <Box sx={{ mt: 1, maxWidth: 480 }}>
                  <DiscoverCardPreview
                    coverTitle={form.coverTitle}
                    summary={form.summary}
                    coverImageUrl={coverSrc}
                    coverAspectRatio="5:2"
                    slideCount={0}
                    imageKey={coverPreviewKey}
                  />
                </Box>
              ) : null}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Sort order"
                value={form.sortOrder}
                onChange={(e) => updateField('sortOrder', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Starts at"
                value={form.startsAt}
                onChange={(e) => updateField('startsAt', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Ends at"
                value={form.endsAt}
                onChange={(e) => updateField('endsAt', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Article #{articleId}
            {getArticleUpdatedAt(article)
              ? ` · Updated ${formatDate(getArticleUpdatedAt(article))}`
              : ''}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Hot take
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Generated with the article (same pipeline as Fan Stories). Shown as the linked Pulse
            poll after the news card. Agree/Disagree-style options preferred unless Product says
            otherwise.
          </Alert>
          <TextField
            fullWidth
            label="Hot take question"
            value={form.hotTake}
            onChange={(e) => updateField('hotTake', e.target.value.slice(0, HOT_TAKE_MAX))}
            margin="dense"
            multiline
            minRows={2}
            helperText={`${String(form.hotTake || '').length}/${HOT_TAKE_MAX} — punchy question fans react to`}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="subtitle2">Options</Typography>
            <Button startIcon={<AddIcon />} size="small" onClick={addHotTakeOption}>
              Add option
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
            {(form.hotTakeOptions || []).filter(Boolean).map((opt) => (
              <Chip key={opt} size="small" label={opt} />
            ))}
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(form.hotTakeOptions || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="body2" color="text.secondary">
                      No options yet — Generate to fill, or add manually.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (form.hotTakeOptions || []).map((opt, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={opt}
                        onChange={(e) => updateHotTakeOption(index, e.target.value)}
                        placeholder="Agree"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeHotTakeOption(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Generation settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Content slides</InputLabel>
                <Select
                  label="Content slides"
                  value={form.slideCount}
                  onChange={(e) => updateField('slideCount', e.target.value)}
                >
                  {SLIDE_COUNT_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Content slides 1–10 (outro added automatically). Images optional. Payload only —
                not a Discover carousel UI.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Editor instructions"
                placeholder={TONE_PLACEHOLDER}
                value={form.generationPrompt}
                onChange={(e) => updateField('generationPrompt', e.target.value)}
                helperText={TONE_HELPER}
                margin="dense"
              />
            </Grid>
            {canEditSources && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Source URL 1"
                    value={form.sourceUrl1}
                    onChange={(e) => updateField('sourceUrl1', e.target.value)}
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Source URL 2"
                    value={form.sourceUrl2}
                    onChange={(e) => updateField('sourceUrl2', e.target.value)}
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Source URL 3"
                    value={form.sourceUrl3}
                    onChange={(e) => updateField('sourceUrl3', e.target.value)}
                    margin="dense"
                  />
                </Grid>
              </>
            )}
            {!canEditSources && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Unpublish to change source URLs before regenerating.
                </Alert>
              </Grid>
            )}
          </Grid>

          {!isPublished && (
            <CoverCropPanel
              sourceImageUrl={cropSourceImageUrl || scrapeHeroUrl || form.coverImageUrl}
              scrapeHeroUrl={scrapeHeroUrl}
              coverTitle={form.coverTitle}
              summary={form.summary}
              disabled={GENERATING_STATUSES.includes(article.status)}
              applying={croppingCover}
              onSourceImageUrlChange={setCropSourceImageUrl}
              onApply={handleCoverCrop}
            />
          )}

          {!isPublished && article.status === 'review' && (
            <PublishChecklist
              coverTitle={form.coverTitle}
              coverImageUrl={form.coverImageUrl}
              summary={form.summary}
              hasPayload={hasPayload}
              hotTake={form.hotTake}
            />
          )}
        </CardContent>
      </Card>

      <Card
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'error.light',
        }}
      >
        <CardContent>
          <Typography variant="subtitle2" color="error" gutterBottom>
            Danger zone
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Soft-delete (archive). Removes the article from the working list and Pulse after
            unpublish/archive. Prefer Delete when it should leave the admin list; Unpublish keeps it
            as review.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteArticle}
            disabled={saving || generating}
          >
            Delete article
          </Button>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ArticleEditor;
